import React, {
  useState, useEffect, useCallback,
} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  VideoLibrary as VideoLibraryIcon,
  Visibility as ViewsIcon,
  Schedule as DurationIcon,
  TrendingUp as TrendingIcon,
  CalendarToday as CalendarIcon,
  VideoCall as LastVideoIcon,
  PlayCircle as LongVideoIcon,
  SmartDisplay as ShortVideoIcon,
  Replay as RefreshIcon,
  NewReleases as NewReleasesIcon,
  Analytics as AnalyticsIcon,
  People as SubscribersIcon,
} from '@mui/icons-material';
import {
  useRecordContext,
  useNotify,
  useRefresh,
  fetchUtils,
} from 'react-admin';
import { BASE_API_URL } from 'config';
import { getAuthToken } from 'authProvider';

function SectionCard({ children, sx }) {
  return (
    <Card sx={{
      p: 2, borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', ...sx,
    }}
    >
      <CardContent>
        {children}
      </CardContent>
    </Card>
  );
}

function StatItem({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {icon}
      <Box>
        <Typography variant="h6" fontWeight="600">{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
    </Stack>
  );
}

export function StatsTabContent() {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const { stats, subscriberCount } = record || {};

  const canUpdateStats = subscriberCount >= 100;

  const handleRefreshClick = useCallback(() => {
    if (!record || !record.id) {
      notify('Cannot refresh stats: Channel ID is missing.', { type: 'warning' });
      return;
    }
    setLoading(true);

    const token = getAuthToken();
    const options = {
      method: 'POST',
      headers: new Headers({
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    };

    fetchUtils.fetchJson(`${BASE_API_URL}channels/${record.id}/update-stats`, options)
      .then(() => {
        // notify('Stats refresh requested. Data will update in a moment.', { type: 'info' });
        setTimeout(() => {
          refresh();
          setLoading(false);
        }, 5000); // give backend time to process
      })
      .catch((error) => {
        notify(`Error requesting stats refresh: ${error.message}`, { type: 'warning', autoHideDuration: 5000 });
        setLoading(false);
      });
  }, [record, notify, refresh]);

  useEffect(() => {
    if (stats && canUpdateStats) {
      const statsDate = new Date(stats.date);
      const now = new Date();
      // 3 days in milliseconds
      const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
      if (now.getTime() - statsDate.getTime() > threeDaysInMs) {
        handleRefreshClick();
      }
    }
  }, [stats, canUpdateStats, handleRefreshClick]);

  if (!stats) {
    return (
      <Box sx={{
        width: '100%',
        p: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 400,
      }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6" color="text.secondary">No statistics available.</Typography>
          {canUpdateStats ? (
            <>
              <Typography color="text.secondary">Click the button to generate the latest stats for this channel.</Typography>
              <Button
                variant="contained"
                onClick={handleRefreshClick}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                sx={{ mt: 2 }}
              >
                {loading ? 'Generating...' : 'Generate Stats'}
              </Button>
            </>
          ) : (
            <Typography color="text.secondary">
              Stats generation is available for channels with 100 or more subscribers.
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  // Helper function to format numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Helper function to format duration (seconds to mm:ss)
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to format date as relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 0) {
      return 'in the future';
    } if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    }

    const years = Math.floor(days / 365);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  };

  const { last30DaysStats } = stats;

  const totalVideos = stats.longVideos + stats.shortVideos;
  const total30DaysVideos = last30DaysStats.longVideos + last30DaysStats.shortVideos;
  const avgViewsTotal = totalVideos > 0 ? Math.round(stats.totalViews / totalVideos) : 0;
  const longVideoAngle = totalVideos > 0 ? (stats.longVideos / totalVideos) * 360 : 0;
  const longVideoAngle30Days = total30DaysVideos > 0 ? (last30DaysStats.longVideos / total30DaysVideos) * 360 : 0;

  // Performance Ratios
  const longVideosHasRecentData = last30DaysStats.longVideosStats.count > 0;
  let longVideosPerformanceRatio = 0;
  let longVideosColor = 'text.secondary';
  let longVideosIsPerformingBetter = false;
  let longVideosPercentageChange = 0;

  if (longVideosHasRecentData) {
    const longVideosRecentAvg = last30DaysStats.longVideosStats.totalViews / last30DaysStats.longVideosStats.count;
    const longVideosTotalAvg = stats.longVideos > 0 ? stats.longVideosStats.totalViews / stats.longVideos : 0;
    if (longVideosTotalAvg > 0) {
      longVideosPerformanceRatio = longVideosRecentAvg / longVideosTotalAvg;
      longVideosIsPerformingBetter = longVideosPerformanceRatio >= 1;
      longVideosColor = longVideosIsPerformingBetter ? 'success.main' : 'error.main';
      longVideosPercentageChange = (longVideosPerformanceRatio - 1) * 100;
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Date & Summary Section */}
        <Grid item xs={12}>
          <SectionCard>
            <Grid container spacing={3} divider={<Divider orientation="vertical" flexItem />}>
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StatItem
                      icon={<CalendarIcon color="primary" />}
                      label={(
                        <>
                          Stats Updated
                          {canUpdateStats && (
                            <IconButton
                              size="small"
                              onClick={handleRefreshClick}
                              disabled={loading}
                              sx={{ ml: 0.5 }}
                            >
                              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </>
                      )}
                      value={formatRelativeTime(stats.date)}
                    />
                  </Stack>
                  <StatItem
                    icon={<SubscribersIcon color="primary" />}
                    label="Total Subscribers"
                    value={formatNumber(subscriberCount)}
                  />
                  <StatItem icon={<LastVideoIcon color="secondary" />} label="Latest Video" value={formatRelativeTime(stats.lastVideoDate)} />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <StatItem icon={<VideoLibraryIcon color="primary" />} label="Total Analyzed Videos" value={totalVideos} />
                  <StatItem icon={<ViewsIcon color="success" />} label="Total Views" value={formatNumber(stats.totalViews)} />
                </Stack>
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>
        {/* Last 30 Days Breakdown */}
        <Grid item xs={12}>
          <SectionCard>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Last 30 Days Breakdown
            </Typography>
            <Grid container spacing={3} divider={<Divider orientation="vertical" flexItem />}>
              <Grid item xs={12} sm={6} md={3}>
                <StatItem icon={<NewReleasesIcon color="primary" />} label="New Videos" value={total30DaysVideos} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatItem icon={<ViewsIcon color="success" />} label="New Views" value={formatNumber(last30DaysStats.totalViews)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatItem icon={<DurationIcon color="warning" />} label="Avg Duration" value={formatDuration(last30DaysStats.averageDuration)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatItem
                  icon={<AnalyticsIcon color="info" />}
                  label="Avg Views/Video"
                  value={total30DaysVideos > 0 ? formatNumber(Math.round(last30DaysStats.totalViews / total30DaysVideos)) : '0'}
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* Video Distribution & Stats */}
        <Grid item xs={12} lg={6}>
          <SectionCard>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Video Distribution
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
              <Box flexShrink={0}>
                <Box
                  sx={{
                    width: 160,
                    height: 160,
                    borderRadius: '50%',
                    background: `conic-gradient(#1976d2 0deg ${longVideoAngle}deg, #9c27b0 ${longVideoAngle}deg 360deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      backgroundColor: 'background.paper',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold">{totalVideos}</Typography>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                  </Box>
                </Box>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Stack spacing={2} flexGrow={1}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Box sx={{
                      width: 16, height: 16, backgroundColor: '#1976d2', borderRadius: '50%',
                    }}
                    />
                    <Typography variant="subtitle1" fontWeight="500">Long Videos</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" pl={3}>
                    {stats.longVideos}
                    {' '}
                    videos (
                    {((stats.longVideos / totalVideos) * 100).toFixed(1)}
                    %)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" pl={3}>
                    {formatNumber(stats.longVideosStats.totalViews)}
                    {' '}
                    views
                  </Typography>
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Box sx={{
                      width: 16, height: 16, backgroundColor: '#9c27b0', borderRadius: '50%',
                    }}
                    />
                    <Typography variant="subtitle1" fontWeight="500">Short Videos</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" pl={3}>
                    {stats.shortVideos}
                    {' '}
                    videos (
                    {((stats.shortVideos / totalVideos) * 100).toFixed(1)}
                    %)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" pl={3}>
                    {formatNumber(stats.shortVideosStats.totalViews)}
                    {' '}
                    views
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>

        {/* Last 30 Days Video Distribution */}
        {total30DaysVideos > 0 && (
          <Grid item xs={12} lg={6}>
            <SectionCard>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Last 30 Days Video Distribution
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
                <Box flexShrink={0}>
                  <Box
                    sx={{
                      width: 160,
                      height: 160,
                      borderRadius: '50%',
                      background: `conic-gradient(#1976d2 0deg ${longVideoAngle30Days}deg, #9c27b0 ${longVideoAngle30Days}deg 360deg)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        backgroundColor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="h5" fontWeight="bold">{total30DaysVideos}</Typography>
                      <Typography variant="caption" color="text.secondary">Total</Typography>
                    </Box>
                  </Box>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Stack spacing={2} flexGrow={1}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Box sx={{
                        width: 16, height: 16, backgroundColor: '#1976d2', borderRadius: '50%',
                      }}
                      />
                      <Typography variant="subtitle1" fontWeight="500">Long Videos</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" pl={3}>
                      {last30DaysStats.longVideos}
                      {' '}
                      videos (
                      {((last30DaysStats.longVideos / total30DaysVideos) * 100).toFixed(1)}
                      %)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" pl={3}>
                      {formatNumber(last30DaysStats.longVideosStats.totalViews)}
                      {' '}
                      views
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Box sx={{
                        width: 16, height: 16, backgroundColor: '#9c27b0', borderRadius: '50%',
                      }}
                      />
                      <Typography variant="subtitle1" fontWeight="500">Short Videos</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" pl={3}>
                      {last30DaysStats.shortVideos}
                      {' '}
                      videos (
                      {((last30DaysStats.shortVideos / total30DaysVideos) * 100).toFixed(1)}
                      %)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" pl={3}>
                      {formatNumber(last30DaysStats.shortVideosStats.totalViews)}
                      {' '}
                      views
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </SectionCard>
          </Grid>
        )}

        {/* Performance Comparison */}
        <Grid item xs={12} lg={6}>
          <SectionCard>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Average Views Performance
            </Typography>
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Table>
                <TableHead sx={{ backgroundColor: 'action.hover' }}>
                  <TableRow>
                    <TableCell>Video Type</TableCell>
                    <TableCell align="center">Total Avg</TableCell>
                    <TableCell align="center">Last 30d Avg</TableCell>
                    <TableCell align="center">% Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LongVideoIcon color="primary" />
                        <Typography variant="body2" fontWeight="500">Long</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{formatNumber(Math.round(stats.longVideosStats.totalViews / stats.longVideos))}</TableCell>
                    <TableCell align="center">
                      {longVideosHasRecentData ? formatNumber(Math.round(last30DaysStats.longVideosStats.totalViews / last30DaysStats.longVideosStats.count)) : '–'}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ color: longVideosColor }}>
                        {longVideosHasRecentData ? (
                          <>
                            <Typography variant="body2" fontWeight="bold">
                              {longVideosPercentageChange > 0 ? '+' : ''}
                              {`${longVideosPercentageChange.toFixed(0)}%`}
                            </Typography>
                            <TrendingIcon sx={{ fontSize: 16, transform: longVideosIsPerformingBetter ? 'none' : 'rotate(180deg)' }} />
                          </>
                        ) : '–'}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ShortVideoIcon color="secondary" />
                        <Typography variant="body2" fontWeight="500">Short</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{formatNumber(Math.round(stats.shortVideosStats.totalViews / stats.shortVideos))}</TableCell>
                    <TableCell align="center">{formatNumber(stats.shortVideos > 0 ? Math.round(stats.shortVideosStats.totalViews / stats.shortVideos) : 0)}</TableCell>
                    <TableCell align="center">–</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
